<template>
    <div>
        <loading-indicator v-if="!entry || !additionalData"/>
        <div v-else class="margin-left-15">
            <template v-if="isServiceEntry && !entry.is_reservation">
                <div class="headline-text font-size-22">
                    {{(entry.salutation ? entry.salutation + ' ' : '') + entry.prename + ' ' + entry.name}}
                    <i v-if="entry.accessibility_required"
                        aria-label="Barrierefreier Zugang wird benötigt." class="material-icons cursor-help" aria-hidden="true"
                        title="Barrierefreier Zugang wird benötigt." role="img">accessible</i>
                </div>

                <div class="spacer spacer-10"/>

                <template v-if="entry.email || entry.telephone">
                    <div v-if="entry.telephone">
                        Tel.:
                        <phone-number :number="entry.telephone" force-link/>
                    </div>

                    <div v-if="entry.email">
                        E-Mail: <a :href="'mailto:' + entry.email">{{entry.email}}</a>
                    </div>
                </template>

                <div v-for="element of customFormElements" v-if="customFormElementsExist">
                    <span class="text-grey" v-text="element.title + ':'"></span>
                    <span v-text="element.value && element.value.trim().length > 0 ? element.value : 'Keine Angabe'"></span>
                </div>

                <div v-if="customFormElementsExist || (entry.email || entry.telephone)" class="spacer spacer-10"/>

                <ul v-if="entry.services!.length" class="font-size-18 text-dark-grey spacer-10-bottom">
                    <template v-for="service in entry.services">
                        <template v-if="service && additionalData.services!.services[service] && !additionalData.services!.services[service].is_deleted">
                            <li>
                                <router-link v-if="service" :to="{name: 'service-view', params: {id: service}}">
                                    {{getServiceName(service, true, additionalData, people[service] ?? 1, showPeople)}}
                                </router-link>
                                <div v-if="showPeople && (people[service] ?? 1) > 1">
                                    {{people[service].toLocaleString()}} Teilnehmer
                                </div>
                            </li>
                        </template>
                        <template v-else>
                            <li>
                                {{getServiceName(service, true, additionalData, people[service] ?? 1, showPeople)}}
                                <div v-if="showPeople && (people[service] ?? 1) > 1">
                                    {{people[service].toLocaleString()}} Teilnehmer
                                </div>
                            </li>
                        </template>
                    </template>
                </ul>
                <div v-else-if="entry.is_anonymized" class="font-size-18 text-dark-grey spacer-10-bottom">
                    Ohne {{(options as ServiceCalendarResourceOptions).serviceType}}
                </div>
            </template>
            <div v-else-if="entry.is_reservation" class="headline-text font-size-22">
                Unbestätigte Reservierungsanfrage
            </div>

            <div>
                <span class="text-grey">Datum:</span>
                <strong v-html="entryTimeString"></strong>
                <i
                    v-if="showPeople && !isServicesReservation"
                    v-tooltip
                    class="material-icons small" aria-hidden="true"
                    title="Dieser Termin wurde für mehrere Teilnehmer gebucht. Die angezeigte Zeitspanne ist daher größer, da sie hintereinander liegende Einzeltermine für jeden Teilnehmer umfasst."
                    role="img">
                    info
                </i>
            </div>

            <template v-if="!isServicesReservation">
                <template v-if="entry.message">
                    <hr>

                    <div class="headline-text font-size-22">Nachricht</div>

                    <div class="break-spaces" v-html="entry.message"/>
                </template>

                <template v-if="entry.internal_note || entry.files && entry.files.length">
                    <hr>

                    <div class="headline-text font-size-22">Interne Bemerkungen</div>

                    <div v-html="entry.internal_note"/>

                    <template v-if="entry.files">
                        <file-list :files="entry.files">
                            <template #additional>
                                <app-popup-download
                                    v-if="entry.files.length > 1"
                                    :create-route="`/services/entry/create-zip-files/id/${entry.id}`"
                                    :download-route="'/services/entry/download-zip-file'"
                                    class="col-12 col-lg-6 flex-vertical-middle">
                                    <template #link="{downloadFiles}">
                                        <a class="button button-secondary button-block spacer-10" @click="downloadFiles">
                                            <i aria-hidden="true" class="material-icons" role="img">file_download</i>
                                            <span>Alle Dateien herunterladen</span>
                                        </a>
                                    </template>
                                </app-popup-download>
                            </template>
                        </file-list>
                    </template>
                </template>
            </template>
        </div>
    </div>
    <portuna-box v-if="isServicesReservation" class="disclaimer default-margin-top">
        Es handelt sich um eine unbestätigte Reservierungsanfrage. Wird die Reservierung nicht innerhalb von 15 Minuten vom Buchenden bestätigt, so wird diese automatisch wieder gelöscht.
    </portuna-box>
</template>

<script setup lang="ts">
    import LoadingIndicator from '@/components/LoadingIndicator.vue';
    import PortunaBox from '@/components/PortunaBox.vue';
    import AppPopupDownload from '@/components/AppPopupDownload.vue';
    import FileList from '@/components/FileList.vue';
    import PhoneNumber from '@/components/PhoneNumber.vue';
    import {useCalendar} from '@/composables/modules/calendar/calendar';
    import {useCalendarEntryViewContent} from '@/composables/modules/calendar/entryViewContent';
    import {
        getServiceName,
        isServicesCalenderEntry,
        TYPE_CALENDAR_UNAVAILABILITY_ENTRY,
    } from '@/library/modules/services/services';
    import {computed, ref, toRefs} from 'vue';

    const props = withDefaults(
        defineProps<{
            entry: IServicesCalendarEntryViewData;
            calendar: CurrentCalendarData | CalendarData;
            resource: CalendarResources;
            additionalData?: AdditionalCalendarData;
        }>(),
        {
            additionalData: () => ({}),
        },
    );

    const {
              entry,
              calendar,
              resource,
              additionalData,
          } = toRefs(props);

    const {
              options,
          } = useCalendar(resource);

    const {
              entryTimeString,
          } = useCalendarEntryViewContent<IServicesCalendarEntryViewData>(entry, calendar);

    const people = ref<Record<string, number>>({});

    const isServiceEntry = computed(() =>
    {
        return isServicesCalenderEntry(props.entry);
    });

    const customFormElementsExist = computed(() =>
    {
        return entry.value!.custom_form_elements && entry.value!.custom_form_elements.trim().length > 0;
    });

    const customFormElements = computed((): IServiceEntryContactCustomFormElement[] =>
    {
        return JSON.parse(entry.value!.custom_form_elements);
    });

    const showPeople = computed(() =>
    {
        return (
            entry.value!.entry_type !== TYPE_CALENDAR_UNAVAILABILITY_ENTRY &&
            (
                entry.value!.allowMultiplePeople ||
                Object.values(people.value).some((value) => value > 1)
            )
        );
    });

    const isServicesReservation = computed(() =>
    {
        return isServiceEntry.value && entry.value!.is_reservation;
    });

    if (
        entry.value!.entry_type !== TYPE_CALENDAR_UNAVAILABILITY_ENTRY &&
        typeof entry.value!.people === 'string'
    )
    {
        people.value = JSON.parse(entry.value!.people);
    }
</script>